import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserDetails } from "../../store/slices/authSlice";
import "../../style/setting.css";
import "../../style/toast.css";
import ChangePasswordIcon from "../../assets/change-password-icon.svg";
import PersonalInformationIcon from "../../assets/personal-information-icon.svg";
import { getUserDetails, updateUserDetails, changePassword } from "../../services/settings-api";
import { showToast } from "../../utils/toast";

const Settings = () => {
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const dispatch = useDispatch();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [userInfo, setUserInfo] = useState({ name: '', email: '', phone: '', role: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const userId = useSelector((state) => state.auth.user?.id);

  useEffect(() => {
    if (userId) {
      loadUserDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadUserDetails = async () => {
    if (!userId) return;
    try {
      const response = await getUserDetails(userId);
      const data = response.data.data;
      setUserInfo({
        name: data.name || '',
        email: data.display_email || '',
        phone: data.mobile || '',
        role: data.admin_role || ''
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleUserInfoChange = (e) => {
    setUserInfo({ ...userInfo, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };

  const handleSaveChanges = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      await updateUserDetails(userId, {
        name: userInfo.name,
        email: userInfo.email,
        admin_role: userInfo.role
      });
      dispatch(fetchUserDetails());
      showToast('Profile updated successfully', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      showToast('New passwords do not match', 'error');
      return;
    }
    setLoading(true);
    try {
      await changePassword({
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      showToast('Password updated successfully', 'success');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      console.error('Error updating password:', error);
      showToast('Failed to update password', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1 className="settings-title">Settings</h1>
        <p className="settings-subtitle">
          Manage your personal details and update your password
        </p>
      </div>

      {/* Personal Information Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="card-icon">
            <img src={PersonalInformationIcon} alt="Personal Information" />
          </div>

          <div>
            <h2 className="card-title">Personal Information</h2>
            <p className="card-description">
              Update your basic profile details
            </p>
          </div>
        </div>

        <div className="form-row">
          <div className="settings-form-group">
            <label className="settings-form-label">Name</label>
            <input type="text" name="name" className={`${isPartsURL ? "form-input-parts" : isAccessoriesURL ? "form-input-acc" : "form-input"}`} value={userInfo.name} onChange={handleUserInfoChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="settings-form-group">
            <label className="settings-form-label">Email Address</label>
            <input
              type="email"
              name="email"
              className={`${isPartsURL ? "form-input-parts" : isAccessoriesURL ? "form-input-acc" : "form-input"}`}
              value={userInfo.email}
              onChange={handleUserInfoChange}
            />
          </div>
          <div className="settings-form-group">
            <label className="settings-form-label">Phone Number</label>
            <input
              type="tel"
              name="phone"
              className={`${isPartsURL ? "form-input-parts" : isAccessoriesURL ? "form-input-acc" : "form-input"}`}
              value={userInfo.phone}
              onChange={handleUserInfoChange}
            />
          </div>
        </div>

        <div className="role-settings-form-group">
          <label className="settings-form-label">Role</label>
          <input
            type="text"
            className="form-input"
            value={userInfo.role}
            disabled
          />
          <p className="form-hint">Role can only be changed by a super admin</p>
        </div>

        <div className="card-actions">
          <button className={`${isPartsURL ? "btn-primary-parts" : isAccessoriesURL ? "btn-primary-acc" : "btn-primary"}`} onClick={handleSaveChanges} disabled={loading}>Save Changes</button>
          <button className="btn-secondary" onClick={loadUserDetails}>Cancel</button>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="card-icon">
            <img src={ChangePasswordIcon} alt="Change Password" />
          </div>
          <div>
            <h2 className="card-title">Change Password</h2>
            <p className="card-description">
              Update your password to keep your account secure
            </p>
          </div>
        </div>

        <div
          className="password-settings-form-group"
          style={{ marginBottom: "22px" }}
        >
          <label className="settings-form-label">Old Password</label>
          <div className="password-input-wrapper">
            <input
              type={showOldPassword ? "text" : "password"}
              name="current_password"
              className={`${isPartsURL ? "form-input-parts" : isAccessoriesURL ? "form-input-acc" : "form-input"}`}
              placeholder="Enter current password"
              value={passwords.current_password}
              onChange={handlePasswordChange}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowOldPassword(!showOldPassword)}
            >
              {showOldPassword ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
                    clipRule="evenodd"
                  />
                  <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path
                    fillRule="evenodd"
                    d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="form-row" style={{ marginBottom: "25px" }}>
          <div className="settings-form-group">
            <label className="settings-form-label">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showNewPassword ? "text" : "password"}
                name="new_password"
                className={`${isPartsURL ? "form-input-parts" : isAccessoriesURL ? "form-input-acc" : "form-input"}`}
                placeholder="Enter new password"
                value={passwords.new_password}
                onChange={handlePasswordChange}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
                      clipRule="evenodd"
                    />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path
                      fillRule="evenodd"
                      d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          <div className="settings-form-group">
            <label className="settings-form-label">Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirm_password"
                className={`${isPartsURL ? "form-input-parts" : isAccessoriesURL ? "form-input-acc" : "form-input"}`}
                placeholder="Confirm new password"
                value={passwords.confirm_password}
                onChange={handlePasswordChange}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
                      clipRule="evenodd"
                    />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path
                      fillRule="evenodd"
                      d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="password-requirements">
          <p>Password Requirements</p>
          <ul>
            <li>Minimum 8 characters</li>
            <li>At least one uppercase letter</li>
            <li>At least one number</li>
            <li>At least one special character</li>
          </ul>
        </div>

        <div className="card-actions">
          <button className={`${isPartsURL ? "btn-primary-parts" : isAccessoriesURL ? "btn-primary-acc" : "btn-primary"}`} onClick={handleUpdatePassword} disabled={loading}>Update Password</button>
          <button className="btn-secondary" onClick={() => setPasswords({ current_password: '', new_password: '', confirm_password: '' })}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
